import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo } from 'react';
import Button from '@jetbrains/ring-ui/components/button/button';
import settings from '@jetbrains/icons/settings';
import trash from '@jetbrains/icons/trash';
import classNames from 'classnames';
import Icon from '@jetbrains/ring-ui/components/icon';
import addIcon from '@jetbrains/icons/add';
import DropdownMenu from '@jetbrains/ring-ui/components/dropdown-menu/dropdown-menu';
import { isArray } from 'lodash';
import { useOpenModal } from '@common/modals/hooks';
import { assignTestId } from '@common/infra/tests/assign-test-id';
import { ProviderModelsView } from './provider-models-view/provider-models-view';
import { ModelConfigurationProblemsBanner } from './model-configuration-problems-banner';
import styles from './provider-models.css';
export function ProviderModels(props) {
    var _a;
    const { modelDialogComponent: DialogComponent, columns: columnsExt, modelConfigurations, unsupportedModelConfigurations, withHeader, createNewModel, modelsController, className } = props;
    const models = modelsController.field.value;
    const onModelsChange = modelsController.field.onChange;
    const { fieldState } = modelsController;
    const notYetAddedConfigurations = useMemo(() => { var _a; return (_a = modelConfigurations === null || modelConfigurations === void 0 ? void 0 : modelConfigurations.filter(conf => models.every(model => model.id !== conf.id))) !== null && _a !== void 0 ? _a : []; }, [modelConfigurations, models]);
    const unsupportedConfigurations = useMemo(() => {
        var _a;
        return (_a = unsupportedModelConfigurations === null || unsupportedModelConfigurations === void 0 ? void 0 : unsupportedModelConfigurations.filter(conf => models.find(model => model.id === conf.id && model.modelName === conf.label))) !== null && _a !== void 0 ? _a : [];
    }, [unsupportedModelConfigurations, models]);
    const openModal = useOpenModal(DialogComponent !== null && DialogComponent !== void 0 ? DialogComponent : (() => null), {});
    const handleAddModel = useCallback(async (type) => {
        if (!DialogComponent && createNewModel) {
            onModelsChange === null || onModelsChange === void 0 ? void 0 : onModelsChange([...models, createNewModel(type)]);
        }
        if (DialogComponent) {
            const newModel = await openModal({
                type,
                model: createNewModel ? createNewModel(type) : undefined,
                modelConfigurations
            });
            if (newModel) {
                onModelsChange === null || onModelsChange === void 0 ? void 0 : onModelsChange([...models, newModel]);
            }
        }
    }, [DialogComponent, createNewModel, onModelsChange, models, openModal, modelConfigurations]);
    const editModel = useCallback(async (modelId) => {
        if (!DialogComponent || !onModelsChange) {
            return;
        }
        const modelIndex = models.findIndex(m => m.id === modelId);
        const model = models[modelIndex];
        const updatedModel = await openModal({ model, type: modelId, modelConfigurations });
        if (updatedModel) {
            const updatedModels = [...models];
            updatedModels[modelIndex] = updatedModel;
            onModelsChange(updatedModels);
        }
    }, [DialogComponent, onModelsChange, models, openModal, modelConfigurations]);
    const removeModel = useCallback((modelId) => {
        onModelsChange === null || onModelsChange === void 0 ? void 0 : onModelsChange(models.filter(m => m.id !== modelId));
    }, [models, onModelsChange]);
    const migrateModel = useCallback(async (modelId) => {
        var _a;
        if (!DialogComponent || !onModelsChange) {
            return;
        }
        const newModelId = (_a = unsupportedConfigurations.find(conf => conf.id === modelId)) === null || _a === void 0 ? void 0 : _a.migrateTo;
        if (!newModelId) {
            return;
        }
        const modelIndex = models.findIndex(m => m.id === modelId);
        const updatedModel = await openModal({
            type: newModelId,
            model: createNewModel ? createNewModel(newModelId) : undefined,
            modelConfigurations
        });
        if (updatedModel) {
            const updatedModels = [...models];
            updatedModels[modelIndex] = updatedModel;
            onModelsChange(updatedModels);
        }
    }, [
        DialogComponent,
        onModelsChange,
        unsupportedConfigurations,
        models,
        openModal,
        createNewModel,
        modelConfigurations
    ]);
    const removeOrMigrateModel = useCallback((modelId) => {
        if (unsupportedConfigurations.find(conf => conf.id === modelId && conf.migrateTo != null)) {
            return migrateModel(modelId);
        }
        return removeModel(modelId);
    }, [migrateModel, removeModel, unsupportedConfigurations]);
    const columns = useMemo(() => {
        return columnsExt.concat({
            id: 'controls',
            getValue: (model) => {
                const configuration = modelConfigurations === null || modelConfigurations === void 0 ? void 0 : modelConfigurations.find(conf => conf.id === model.id);
                const canBeEdited = DialogComponent && !(configuration === null || configuration === void 0 ? void 0 : configuration.readOnly);
                const canBeRemoved = !(configuration === null || configuration === void 0 ? void 0 : configuration.readOnly) && !(configuration === null || configuration === void 0 ? void 0 : configuration.required);
                return (_jsx(TableControls, { onEditClick: canBeEdited ? () => editModel(model.id) : undefined, onRemoveClick: canBeRemoved ? () => removeModel(model.id) : undefined }));
            }
        });
    }, [DialogComponent, modelConfigurations, editModel, removeModel, columnsExt]);
    let action;
    if (notYetAddedConfigurations.length === 1) {
        action = (_jsx(AddModelButton, { modelConfigurations: notYetAddedConfigurations, onAddModel: handleAddModel }));
    }
    else if (notYetAddedConfigurations.length > 1) {
        action = (_jsx(AddModelDropdownMenu, { modelConfigurations: notYetAddedConfigurations, onAddModel: handleAddModel }));
    }
    const arrayErrorMsg = (_a = fieldState.error) === null || _a === void 0 ? void 0 : _a.message;
    const modelsErrorMsg = extractFirstModelErrorMsg(fieldState.error);
    const errorMsg = fieldState.isDirty ? arrayErrorMsg !== null && arrayErrorMsg !== void 0 ? arrayErrorMsg : modelsErrorMsg : undefined;
    let infoMsg = !errorMsg && models.length === 0 ? 'No models configured yet' : undefined;
    if (!errorMsg && !infoMsg && modelsErrorMsg) {
        infoMsg = modelsErrorMsg;
    }
    const missingRequiredModels = notYetAddedConfigurations.filter(m => m.required);
    const missingRecommendedModels = notYetAddedConfigurations.filter(m => m.recommended);
    const removedModels = unsupportedConfigurations.filter(m => m.removed);
    const migratedModels = unsupportedConfigurations.filter(m => !m.removed);
    return (_jsxs(_Fragment, { children: [_jsx(ModelConfigurationProblemsBanner, { misconfiguredModels: missingRequiredModels, onModelAction: handleAddModel, singularTextTemplate: modelLabel => `Required model ${modelLabel} is missing. Provider configuration may not be working correctly.`, pluralTextTemplate: modelLabels => `Required models ${modelLabels} are missing. Provider configuration may not be working correctly.`, actionTextTemplate: modelConfig => `Add ${modelConfig.label} model` }), _jsx(ModelConfigurationProblemsBanner, { variant: "neutral", misconfiguredModels: missingRecommendedModels, onModelAction: handleAddModel, singularTextTemplate: modelLabel => `Recommended model ${modelLabel} is missing. Provider configuration may not be working correctly.`, pluralTextTemplate: modelLabels => `Recommended models ${modelLabels} are missing. Provider configuration may not be working correctly.`, actionTextTemplate: modelConfig => `Add ${modelConfig.label} model` }), _jsx(ModelConfigurationProblemsBanner, { variant: "neutral", misconfiguredModels: migratedModels, onModelAction: migrateModel, singularTextTemplate: modelLabel => `Configured model ${modelLabel} needs migration. Provider configuration may not be working correctly.`, pluralTextTemplate: modelLabels => `Configured models ${modelLabels} need migration. Provider configuration may not be working correctly.`, actionTextTemplate: modelConfig => `Migrate ${modelConfig.label} model` }), _jsx(ModelConfigurationProblemsBanner, { variant: "warning", misconfiguredModels: removedModels, onModelAction: removeOrMigrateModel, singularTextTemplate: modelLabel => `Configured model ${modelLabel} was deprecated and should be removed or migrated. Provider configuration may not be working correctly.`, pluralTextTemplate: modelLabels => `Configured models ${modelLabels} were deprecated and should be removed or migrated. Provider configuration may not be working correctly.`, actionTextTemplate: modelConfig => `${modelConfig.migrateTo === null ? 'Remove' : 'Migrate'} ${modelConfig.label} model` }), _jsx(ProviderModelsView, { className: classNames(styles.wrapper, className), models: models, columns: columns, errorMsg: errorMsg, infoMsg: infoMsg, withHeader: withHeader, actions: action })] }));
}
function TableControls(props) {
    const { onEditClick, onRemoveClick } = props;
    return (_jsxs("div", { className: styles.tableControls, children: [onEditClick && (_jsx(Button, { className: classNames(styles.control, styles.edit), icon: settings, onClick: onEditClick, ...assignTestId('edit-model') })), onRemoveClick && (_jsx(Button, { className: classNames(styles.control, styles.remove), icon: trash, onClick: onRemoveClick, ...assignTestId('remove-model') }))] }));
}
function AddModelButton(props) {
    var _a, _b, _c, _d;
    const { modelConfigurations, onAddModel } = props;
    const handleBtnClick = useCallback(() => onAddModel(modelConfigurations[0].id), [onAddModel, modelConfigurations]);
    const modelName = (_b = (_a = modelConfigurations[0]) === null || _a === void 0 ? void 0 : _a.label) !== null && _b !== void 0 ? _b : (_c = modelConfigurations[0]) === null || _c === void 0 ? void 0 : _c.id;
    const modelId = (_d = modelConfigurations[0]) === null || _d === void 0 ? void 0 : _d.id;
    return (_jsxs(Button, { ...assignTestId('add-model-btn'), text: true, inline: true, onClick: handleBtnClick, children: [_jsx(Icon, { glyph: addIcon }), _jsx("span", { className: styles.addBtnText, ...assignTestId(modelId), children: modelName ? `Add ${modelName} model` : 'Add model' })] }));
}
function AddModelDropdownMenu(props) {
    const { onAddModel, modelConfigurations } = props;
    const dropdownMenuItems = useMemo(() => modelConfigurations.map(conf => {
        var _a;
        return ({
            label: (_a = conf.label) !== null && _a !== void 0 ? _a : conf.id,
            onClick: () => onAddModel(conf.id),
            ...assignTestId('add-model-dropdown-item', conf.id)
        });
    }), [onAddModel, modelConfigurations]);
    return (_jsx(DropdownMenu, { ...assignTestId('add-model-dropdown-menu'), anchor: _jsxs(Button, { ...assignTestId('add-model-dropdown-anchor'), text: true, inline: true, children: [_jsx(Icon, { glyph: addIcon }), _jsx("span", { className: styles.addBtnText, children: "Add Model" })] }), data: dropdownMenuItems }));
}
function extractFirstModelErrorMsg(fieldError) {
    var _a;
    if (isArray(fieldError)) {
        try {
            const firstError = fieldError.find(Boolean);
            if (firstError) {
                return (_a = Object.values(firstError)[0]) === null || _a === void 0 ? void 0 : _a.message;
            }
        }
        catch (e) {
            return undefined;
        }
    }
    return undefined;
}
